import {
  PRODUCT_IDS, PRODUCT_TYPES, EVONE_CLIENT_ID, AUTH_OAUTH2_LOGIN_API,
} from 'constants/AppConstants';
import {
  SERVICE_REQUEST_ROUTE,
  EXPORT_DATA_ROUTE,
  SUBMIT_PAYMENT_OPTION_SUCCESS_ROUTE,
  ANALYTICS_DASHBOARD_ROUTE,
  SUBMIT_PAYMENT_OPTION_ROUTE,
  REPORT_DETAIL_ROUTE,
  SITEMPAP_ROUTE,
  UPLOAD_INFORMATION_ROUTE,
  SUPPORT_ROUTE,
  TEXT_ALERT_SETTINGS_ROUTE,
  PITCH_EDITOR_ROUTE,
  ORDERS_SUBMITTED_ROUTE,
  SITEMAP_SUBMITTED_ROUTE,
  ORDER_PREFERENCES_ROUTE,
  UPGRADE_ROUTE,
  UPGRADED_REPORT_ROUTE,
  CONTACT_INFORMATION_ROUTE,
  BILLING_INFORMATION_ROUTE, REPORT_CHANGE_ROUTE,
  ADDRESS_VERIFICATION_ROUTE, MANAGE_PAYMENT_OPTIONS_ROUTE, SERVICE_AGREEMENT_ROUTE, ACH_AUTHORIZATION_ROUTE,
  ACCOUNT_DELETION_ROUTE, ORDER_HISTORY_ROUTE, DASHBOARD_ROUTE, FINALIZE_ORDER_ROUTE,
  CONSUMPTION_ROUTE,
  BILLING_INFORMATION_ADD_PAYMENT_METHOD_ROUTE, BILLING_INFORMATION_ADD_CARD_ROUTE, BILLING_INFORMATION_ADD_ACH_ROUTE, // #genai
} from 'constants/RouteConstants';
import { getTabListItems } from 'features/orderPreferences/OrderPreferences.utils';
import i18n from 'i18n';
import history from 'store/CreateHistory';
import { getTokenClaims } from './auth.utils';
import { logInfo } from './logger.utils';
/**
 * gets the user details object stored in local storage by viewpoint applicaton
 */
export const getUserDetails = () => {
  const user = JSON.parse(localStorage.getItem('userDetails'));
  return user || {};
};

export const getKeyByValue = (object, value) => Object.keys(object).find((key) => object[key] === value);

export const arrayBufferToBase64 = (buffer) => {
  const binary = Array.from(new Uint8Array(buffer)).map((byte) => String.fromCharCode(byte)).join('');
  return window.btoa(binary);
};

export const base64ToArrayBuffer = (base64String) => {
  const binaryString = window.atob(base64String);
  return new Uint8Array(Array.from(binaryString, (char) => char.charCodeAt(0)));
};

const encryptWithWebCrypto = async (original, key, iv) => {
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    key,
    { name: 'AES-CBC', length: 256 },
    false,
    ['encrypt'],
  );

  const encodedText = new TextEncoder().encode(original);

  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: 'AES-CBC', iv },
    cryptoKey,
    encodedText,
  );

  return arrayBufferToBase64(encryptedBuffer);
};

export const encryptAndEncode = async (data, key, iv) => {
  if (!data) {
    throw new Error('data to be encrypted should not be null or empty.');
  }

  const keyArrayBuffer = base64ToArrayBuffer(key);
  const ivArrayBuffer = base64ToArrayBuffer(iv);
  const encrypted = await encryptWithWebCrypto(data, keyArrayBuffer, ivArrayBuffer);

  // Encode the encrypted data to base64.
  return window.btoa(encrypted);
};

const decryptWithWebCrypto = async (data, key, iv) => {
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    key,
    { name: 'AES-CBC', length: 256 },
    false,
    ['decrypt'],
  );

  const decrypted = await window.crypto.subtle.decrypt(
    { name: 'AES-CBC', iv },
    cryptoKey,
    base64ToArrayBuffer(data),
  );

  return new TextDecoder().decode(decrypted);
};

export const decodeAndDecrypt = async (data, key, iv) => {
  const decoded = window.atob(data);

  const keyArrayBuffer = base64ToArrayBuffer(key);
  const ivArrayBuffer = base64ToArrayBuffer(iv);

  return decryptWithWebCrypto(decoded, keyArrayBuffer, ivArrayBuffer);
};

export const scrollToTop = () => {
  const headerElement = document.getElementsByTagName('header')[0];
  if (headerElement?.scrollIntoView) headerElement.scrollIntoView({ behavior: 'smooth' });
};

export const convertFileToBase64 = (file) => new Promise((resolve, reject) => {
  const fileReader = new FileReader();
  fileReader.readAsDataURL(file);

  fileReader.onload = () => {
    resolve(fileReader.result);
  };

  fileReader.onerror = () => {
    reject(new Error(fileReader.error?.message || 'File read failed'));
  };
});

export const isObjectEmpty = (object) => !object || Object.keys(object).length === 0;
/**
* Helper function to check if there's meaningful expandable content
* @param {Object} expandedRow - The expanded row data object
* @returns {boolean} - True if there's expandable content (claim info or property data)
*/
export const hasExpandableContent = (expandedRow) => {
  if (!expandedRow || isObjectEmpty(expandedRow)) return false;

  // Check if there are any claim info properties other than reportId, completeAddress, and hasPropertyData
  const claimInfoKeys = Object.keys(expandedRow).filter(
    (key) => key !== 'reportId' && key !== 'completeAddress' && key !== 'hasPropertyData' && expandedRow[key],
  );

  // Check if there's property data available
  const hasPropertyData = expandedRow.hasPropertyData === true;

  // Return true if there's either claim info or property data
  return claimInfoKeys.length > 0 || hasPropertyData;
};
// #genai Keep strict switch matching and original case order while limiting each switch's size.
const NO_PRODUCT_TYPE_MATCH = Symbol('NO_PRODUCT_TYPE_MATCH');

const getLegacyProductType = (product) => {
  switch (product) {
    case PRODUCT_IDS.CATClaimsReadyCommercial:
    case PRODUCT_IDS.CATClaimsReadyResidential:
    case PRODUCT_IDS.CATReadyCommercial:
    case PRODUCT_IDS.CATReadyResidential:
      return PRODUCT_TYPES.CATReady;

    case PRODUCT_IDS.EagleViewXML:
      return PRODUCT_TYPES.EagleViewXML;

    case PRODUCT_IDS.AA2D:
    case PRODUCT_IDS.EC2DCommercial:
    case PRODUCT_IDS.EC2DResidential:
      return PRODUCT_TYPES.D2;

    case PRODUCT_IDS.AA3DCommercial:
    case PRODUCT_IDS.AA3DResidential:
    case PRODUCT_IDS.EC3DCommercial:
    case PRODUCT_IDS.EC3DResidential:
      return PRODUCT_TYPES.D3;

    case PRODUCT_IDS.Images:
      return PRODUCT_TYPES.Images;

    case PRODUCT_IDS.InstallerReport:
      return PRODUCT_TYPES.InstallerReport;

    case PRODUCT_IDS.ParkingLot:
      return PRODUCT_TYPES.ParkingLot;

    case PRODUCT_IDS.ECPremiumCommercial:
    case PRODUCT_IDS.ECPremiumResidential:
    case PRODUCT_IDS.PremiumAWCommercial:
    case PRODUCT_IDS.PremiumAWResidential:
    case PRODUCT_IDS.PremiumCommercial:
    case PRODUCT_IDS.PremiumResidential:
    case PRODUCT_IDS.ClaimsAssignmentService:
    case PRODUCT_IDS.Roof:
      return PRODUCT_TYPES.Premium;

    case PRODUCT_IDS.BidPerfect:
    case PRODUCT_IDS.BidPerfectCommercial:
      return PRODUCT_TYPES.BidPerfect;

    case PRODUCT_IDS.OnSiteValidate:
      return PRODUCT_TYPES.OnSiteValidate;

    case PRODUCT_IDS.ClaimsReadyCommercial:
    case PRODUCT_IDS.ClaimsReadyResidential:
      return PRODUCT_TYPES.ClaimsReady;

    case PRODUCT_IDS.SketchOnly:
      return PRODUCT_TYPES.SketchOnly;

    case PRODUCT_IDS.EagleViewInformEssentialsPlusforCommercial:
    case PRODUCT_IDS.EagleViewInformEssentialsPlus:
    case PRODUCT_IDS.SunSitePlusResidential:
    case PRODUCT_IDS.SunSitePlusCommercial:
    case PRODUCT_IDS.EagleViewInformAdvanced:
    case PRODUCT_IDS.EagleViewInformAdvancedforCommercial:
    case PRODUCT_IDS.RevealSunSiteResidential:
    case PRODUCT_IDS.RevealSunSiteCommercial:
    case PRODUCT_IDS.TrueDesignforSales:
    case PRODUCT_IDS.TrueDesignforPlanning:
    case PRODUCT_IDS.SolarValue:
    case PRODUCT_IDS.SolarView:
      return PRODUCT_TYPES.Solar;

    default:
      return NO_PRODUCT_TYPE_MATCH;
  }
};

const getCurrentProductType = (product) => {
  switch (product) {
    case PRODUCT_IDS.StandardResidential:
      return PRODUCT_TYPES.Standard;

    case PRODUCT_IDS.WallsAddOn:
    case PRODUCT_IDS.PremiumResidentialWallonly:
    case PRODUCT_IDS.PremiumCommercialWallonly:
      return PRODUCT_TYPES.Walls;

    case PRODUCT_IDS.WallsLite:
    case PRODUCT_IDS.WallsLiteAddOn:
      return PRODUCT_TYPES.WallsLite;

    case PRODUCT_IDS.WallsLiteV2:
    case PRODUCT_IDS.WallsLiteV2AddOn:
      return PRODUCT_TYPES.WallsLiteV2;

    case PRODUCT_IDS.ResWallWinDoor:
    case PRODUCT_IDS.ResWallWinDoorAddOn:
      return PRODUCT_TYPES.ResWallWinDoor;

    case PRODUCT_IDS.ResWallsFKALite:
    case PRODUCT_IDS.ResWallsFKALiteAddOn:
      return PRODUCT_TYPES.ResWallsFKALite;

    case PRODUCT_IDS.Xactware:
      return PRODUCT_TYPES.Xactware;

    case PRODUCT_IDS.RiskManagementResidential:
    case PRODUCT_IDS.RiskManagementCommercial:
      return PRODUCT_TYPES.RiskManagement;

    case PRODUCT_IDS.RoofGeometryResidential:
      return PRODUCT_TYPES.RoofGeometry;

    case PRODUCT_IDS.Estimate:
    case PRODUCT_IDS.CarrierEstimate:
    case PRODUCT_IDS.MaterialList:
      return PRODUCT_TYPES.Estimate;

    case PRODUCT_IDS.ExpressDelivery:
      return PRODUCT_TYPES.ExpressDelivery;
    case PRODUCT_IDS.RegularDelivery:
      return PRODUCT_TYPES.RegularDelivery;
    case PRODUCT_IDS.SameDayDelivery:
      return PRODUCT_TYPES.SameDayDelivery;
    case PRODUCT_IDS.ThreeHourDelivery:
      return PRODUCT_TYPES.ThreeHourDelivery;

    case PRODUCT_IDS.PictometryPremium:
      return PRODUCT_TYPES.Complete;
    case PRODUCT_IDS.PictometryStandard:
      return PRODUCT_TYPES.Partial;

    case PRODUCT_IDS.QuickSquaresResidential:
    case PRODUCT_IDS.QuickSquaresCommercial:
    case PRODUCT_IDS.QuickSquaresMultiFamily:
      return PRODUCT_TYPES.QuickSquares;
    case PRODUCT_IDS.QuickDelivery:
      return PRODUCT_TYPES.QuickDelivery;
    case PRODUCT_IDS.QuickSquaresExtendedCoverage:
      return PRODUCT_TYPES.QuickSquaresExtendedCoverage;

    case PRODUCT_IDS.GutterResidential:
    case PRODUCT_IDS.GutterCommercial:
      return PRODUCT_TYPES.Gutter;

    case PRODUCT_IDS.DiagramResidential:
    case PRODUCT_IDS.DiagramCommercial:
      return PRODUCT_TYPES.Diagram;

    case PRODUCT_IDS.BlueprintResidential:
      return PRODUCT_TYPES.Blueprint;

    case PRODUCT_IDS.RevealConditionResidential:
      return PRODUCT_TYPES.RevealCondition;
    case PRODUCT_IDS.FullHouse:
      return PRODUCT_TYPES.WholeHome;
    case PRODUCT_IDS.RevealRiskResidential:
      return PRODUCT_TYPES.RevealRisk;
    case PRODUCT_IDS.PropertyData:
      return PRODUCT_TYPES.NotSet;

    default:
      return PRODUCT_TYPES.NotSet;
  }
};

export const getProductType = (product) => {
  const legacyProductType = getLegacyProductType(product);
  // #genai The sentinel distinguishes an unmatched value from a legitimate NotSet result.
  return legacyProductType === NO_PRODUCT_TYPE_MATCH
    ? getCurrentProductType(product)
    : legacyProductType;
};

export const newEmptyArray = (length, value = '') => new Array(length).fill(value);

export const camelCaseToWords = (str) => str.replaceAll(/([A-Z])/g, ' $1').toLowerCase();

// #genai Phase 2: `isPhase2Active` toggles the /billing-information page's title + breadcrumb from
// "Billing Information" to "Payment Methods (Billing)" (Figma 885:9553). Every non-billing route is
// unaffected, and Phase 1 users (flag OFF or non-ACH-eligible) keep the original label.
export const getTitleHeaderProps = (pathname, isPhase2Active = false) => {
  const dashboardBreadcrumb = { title: i18n.t('Dashboard'), link: DASHBOARD_ROUTE };
  const orderHistoryBreadcrumb = { title: i18n.t('common__order_history'), link: ORDER_HISTORY_ROUTE };
  const managePaymentOptionsBreadcrumb = { title: i18n.t('common__manage_payment_options'), link: MANAGE_PAYMENT_OPTIONS_ROUTE };
  const analyticsDashboardBreadcrumb = { title: i18n.t('common__analytics'), link: ANALYTICS_DASHBOARD_ROUTE };

  switch (pathname) {
    case FINALIZE_ORDER_ROUTE:
      return {
        title: i18n.t('common_finalize_order'),
        breadcrumbLinks: [dashboardBreadcrumb],
      };
    case ORDERS_SUBMITTED_ROUTE: return { title: i18n.t('common_order_submitted') };
    case SITEMAP_SUBMITTED_ROUTE:
      return {
        title: i18n.t('common_order_submitted'),
        breadcrumbLinks: [dashboardBreadcrumb],
      };
    case ORDER_HISTORY_ROUTE:
      return {
        title: i18n.t('common__order_history'),
        breadcrumbLinks: [dashboardBreadcrumb],
      };
    case SUPPORT_ROUTE:
      return {
        title: i18n.t('common_support'),
        breadcrumbLinks: [dashboardBreadcrumb],
      };
    case DASHBOARD_ROUTE:
      return {
        title: i18n.t('Dashboard'),
      };
    case CONTACT_INFORMATION_ROUTE:
      return {
        title: i18n.t('contact_information'),
        breadcrumbLinks: [dashboardBreadcrumb],
      };
    case ORDER_PREFERENCES_ROUTE:
      return {
        title: i18n.t('OrderPreferences'),
        breadcrumbLinks: [
          dashboardBreadcrumb,
          { title: i18n.t('OrderPreferences'), link: ORDER_PREFERENCES_ROUTE },
        ],
        finalBreadcrumb: getTabListItems().find((item) => history.location.pathname === item.url)?.label,
      };
    case BILLING_INFORMATION_ROUTE:
      return {
        // #genai Phase 2 (Figma 885:9553) renames the page + final breadcrumb to
        // "Payment Methods (Billing)"; Phase 1 keeps the original "Billing Information".
        title: i18n.t(isPhase2Active ? 'billing_information__payment_methods_billing' : 'billing_information'),
        breadcrumbLinks: [dashboardBreadcrumb],
      };
    // #genai Phase 2 add-payment-method routes — breadcrumb: Dashboard / Payment Methods (Billing) / Add ...
    case BILLING_INFORMATION_ADD_PAYMENT_METHOD_ROUTE:
    case BILLING_INFORMATION_ADD_CARD_ROUTE:
    case BILLING_INFORMATION_ADD_ACH_ROUTE: {
      const paymentMethodsBillingBreadcrumb = { // #genai
        title: i18n.t('billing_information__payment_methods_billing'),
        link: BILLING_INFORMATION_ROUTE,
      };
      // #genai The dedicated add pages have method-specific H1 titles per Figma:
      //   add-ACH  → "Add Bank Payment Method" (269:20728)
      //   add-card → "Add Credit Card Payment Method" (269:21301)
      // The card frame keeps the generic "Add Payment Method" final breadcrumb crumb.
      // #genai Mobile Figmas for these add screens (269:21887 / 269:21702 / 269:21589) have NO blue
      // hero — the white card carries its own centered "Add Payment Method" sub-header. hideOnMobile
      // collapses the TitleHeader on <640px while keeping the desktop hero + breadcrumbs intact.
      if (pathname === BILLING_INFORMATION_ADD_ACH_ROUTE) {
        return {
          title: i18n.t('billing_information__add_bank_payment_method'),
          breadcrumbLinks: [dashboardBreadcrumb, paymentMethodsBillingBreadcrumb],
          hideOnMobile: true,
        };
      }
      if (pathname === BILLING_INFORMATION_ADD_CARD_ROUTE) {
        return {
          title: i18n.t('billing_information__add_credit_card_payment_method'),
          finalBreadcrumb: i18n.t('billing_information__add_payment_method'),
          breadcrumbLinks: [dashboardBreadcrumb, paymentMethodsBillingBreadcrumb],
          hideOnMobile: true,
        };
      }
      return {
        title: i18n.t('billing_information__add_payment_method'),
        breadcrumbLinks: [dashboardBreadcrumb, paymentMethodsBillingBreadcrumb],
      };
    }
    case TEXT_ALERT_SETTINGS_ROUTE:
      return {
        title: i18n.t('common__text_alert_settings'),
        breadcrumbLinks: [dashboardBreadcrumb],
      };
    case MANAGE_PAYMENT_OPTIONS_ROUTE:
      return {
        title: i18n.t('common__manage_payment_options'),
        breadcrumbLinks: [dashboardBreadcrumb],
      };
    case SUBMIT_PAYMENT_OPTION_ROUTE:
    case SUBMIT_PAYMENT_OPTION_SUCCESS_ROUTE:
      return {
        title: i18n.t('common__submit_payment_option'),
        breadcrumbLinks: [dashboardBreadcrumb, managePaymentOptionsBreadcrumb],
      };
    case REPORT_DETAIL_ROUTE:
      return {
        title: i18n.t('report_details__title_header'),
        breadcrumbLinks: [dashboardBreadcrumb, orderHistoryBreadcrumb],
      };
    case SITEMPAP_ROUTE: return { title: i18n.t('common_sitemap') };
    case UPGRADE_ROUTE:
      return {
        title: i18n.t('common_upgrade_report'),
        breadcrumbLinks: [orderHistoryBreadcrumb],
      };
    case UPGRADED_REPORT_ROUTE:
      return {
        title: i18n.t('upgraded__report'),
        breadcrumbLinks: [orderHistoryBreadcrumb],
      };
    case PITCH_EDITOR_ROUTE:
      return {
        title: i18n.t('pitch_editor__title'),
        breadcrumbLinks: [orderHistoryBreadcrumb],
      };
    case REPORT_CHANGE_ROUTE:
      return {
        title: i18n.t('report_change__title'),
        breadcrumbLinks: [orderHistoryBreadcrumb],
      };
    case ADDRESS_VERIFICATION_ROUTE:
      return {
        title: i18n.t('common_identify_property'),
        breadcrumbLinks: [orderHistoryBreadcrumb],
      };
    case ACCOUNT_DELETION_ROUTE: return { title: i18n.t('accounts_deletion') };
    case SERVICE_AGREEMENT_ROUTE: return { title: i18n.t('service_agreement') };
    case ACH_AUTHORIZATION_ROUTE: return { title: i18n.t('ach_authorization') }; // #genai
    case SERVICE_REQUEST_ROUTE: return { title: i18n.t('common__service_request') };
    case UPLOAD_INFORMATION_ROUTE:
      return {
        title: i18n.t('common__upload_information'),
        breadcrumbLinks: [orderHistoryBreadcrumb],
      };
    case ANALYTICS_DASHBOARD_ROUTE: return { title: i18n.t('common__analytics') };
    case EXPORT_DATA_ROUTE: return {
      title: i18n.t('common__export_data'),
      breadcrumbLinks: [analyticsDashboardBreadcrumb],
    };
    case CONSUMPTION_ROUTE:
      return {
        title: i18n.t('consumption_details'),
        breadcrumbLinks: [dashboardBreadcrumb],
      };
    default: return null;
  }
};

export const retryAPI = async (api, retryCount = 1) => {
  const attempt = async (retry) => {
    try {
      const response = await api();
      if (response.ok) {
        return response;
      }
      if (retry < retryCount) {
        return attempt(retry + 1);
      }
      return response;
    } catch (error) {
      if (retry < retryCount) {
        return attempt(retry + 1);
      }
      throw error;
    }
  };
  return attempt(0);
};

export const constructUrlWithUserDetails = (baseUrl) => {
  const userDetails = getUserDetails();
  const hasOrgId = Boolean(userDetails?.organization?.id);
  const hasUserName = Boolean(userDetails?.userName);

  if (hasOrgId && hasUserName) {
    return `${baseUrl}?organizationId=${userDetails.organization.id}&userId=${userDetails.userName}`;
  }
  return baseUrl;
};

export const getUserMenuOptions = (isOrderingDisabled = false, hasTrueDesignAppEntitlement = false) => (
  [...(isOrderingDisabled ? [] : [{
    name: 'OrderPreferences',
    link: ORDER_PREFERENCES_ROUTE,
  },
  ...(hasTrueDesignAppEntitlement ? [{
    name: 'TrueDesign Settings',
    link: '/settings',
    base: '/truedesign',
  }] : []),
  {
    name: 'BillingInformation',
    link: BILLING_INFORMATION_ROUTE,
  }]),
  {
    name: 'ContactInformation',
    link: CONTACT_INFORMATION_ROUTE,
  },
  {
    name: 'Support',
    link: SUPPORT_ROUTE,
  }]
);

export const trackUserEvent = async (action, actionContext = {}) => {
  const userClaims = getTokenClaims();
  const eventData = {
    action,
    userContext: {
      initiatorUserId: userClaims?.initiatorUserId || '',
      initiatorUserName: userClaims?.initiatorUserName || '',
      initiatorOrganizationId: userClaims?.initiatorOrganizationId || '',
      userId: userClaims?.sub || '',
      organizationId: userClaims?.organizationId || '',
    },
    pageContext: {
      url: globalThis.location.href,
    },
    actionContext, // add any additional info required
    metadata: {
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    },
  };

  // Send to Rollbar
  logInfo('User Event Tracking', eventData);
};

export const buildEvoneOauthLoginUrl = (evoneAppUrl) => {
  const params = new URLSearchParams({
    redirect_uri: evoneAppUrl,
    client_id: EVONE_CLIENT_ID,
  });
  return `${AUTH_OAUTH2_LOGIN_API}?${params.toString()}`;
};
